import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BookmarkSimple as BookmarkPlus } from "@phosphor-icons/react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from "@/hooks/useTranslation";

interface SaveSearchDialogProps {
  currentFilters: Record<string, any>;
  searchQuery?: string;
}

export const SaveSearchDialog = ({ currentFilters, searchQuery }: SaveSearchDialogProps) => {
  const [open, setOpen] = useState(false);
  const [searchName, setSearchName] = useState("");
  const [frequency, setFrequency] = useState<"instant" | "daily" | "weekly">("daily");
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();
  const { t } = useTranslation();

  const handleSave = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      toast({
        variant: "destructive",
        title: t('save_search.auth_required_title'),
        description: t('save_search.auth_required_desc'),
      });
      return;
    }

    if (!searchName.trim()) {
      toast({
        variant: "destructive",
        title: t('save_search.name_required_title'),
        description: t('save_search.name_required_desc'),
      });
      return;
    }

    setSaving(true);
    try {
      const { error } = await supabase.from("price_alerts").insert({
        user_id: user.id,
        alert_name: searchName,
        search_name: searchQuery || "",
        filter_json: currentFilters,
        notification_frequency: frequency,
        make: currentFilters.marca || null,
        model: currentFilters.model || null,
        year_min: currentFilters.anMin ? parseInt(currentFilters.anMin) : null,
        year_max: currentFilters.anMax ? parseInt(currentFilters.anMax) : null,
        price_max: currentFilters.pretMax ? parseFloat(currentFilters.pretMax) : null,
        fuel_type: currentFilters.combustibil || null,
        transmission: currentFilters.transmisie || null,
        location: currentFilters.locatie || null,
        vehicle_type: currentFilters.tip || null,
        active: true,
      });

      if (error) throw error;

      toast({
        title: t('save_search.success_title'),
        description: t('save_search.success_description'),
      });

      setOpen(false);
      setSearchName("");
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: t('save_search.error_title'),
        description: error.message || t('save_search.error_desc'),
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2">
          <BookmarkPlus className="h-4 w-4" />
          {t('save_search.button')}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('save_search.title')}</DialogTitle>
          <DialogDescription>
            {t('save_search.description')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="search-name">{t('save_search.name_label')}</Label>
            <Input
              id="search-name"
              placeholder={t('save_search.name_placeholder')}
              value={searchName}
              onChange={(e) => setSearchName(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="frequency">{t('save_search.frequency_label')}</Label>
            <Select value={frequency} onValueChange={(v: any) => setFrequency(v)}>
              <SelectTrigger id="frequency">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="instant">{t('save_search.frequency_instant')}</SelectItem>
                <SelectItem value="daily">{t('save_search.frequency_daily')}</SelectItem>
                <SelectItem value="weekly">{t('save_search.frequency_weekly')}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="rounded-lg bg-muted p-4 space-y-1">
            <p className="text-sm font-medium">{t('save_search.filters_applied')}</p>
            <ul className="text-xs text-muted-foreground space-y-1">
              {searchQuery && <li>• {t('save_search.search_term')} "{searchQuery}"</li>}
              {currentFilters.marca && <li>• {t('save_search.make')} {currentFilters.marca}</li>}
              {currentFilters.model && <li>• {t('save_search.model')} {currentFilters.model}</li>}
              {currentFilters.anMin && <li>• {t('save_search.year_min')} {currentFilters.anMin}</li>}
              {currentFilters.pretMax && <li>• {t('save_search.price_max')} {currentFilters.pretMax}€</li>}
              {currentFilters.combustibil && <li>• {t('save_search.fuel')} {currentFilters.combustibil}</li>}
              {currentFilters.locatie && <li>• {t('save_search.location')} {currentFilters.locatie}</li>}
              {Object.keys(currentFilters).length === 0 && <li>• {t('save_search.all_listings')}</li>}
            </ul>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            {t('save_search.cancel')}
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? t('save_search.saving') : t('save_search.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
